<template>
  <SatellitesPage>
    <template v-if="rows.length" #actions>
      <button @click.prevent="create" class="btn _bg-green sat-btn-inline" :disabled="creating">
        <img v-if="creating" src="@/assets/images/rolling-button.svg" class="loader-button" alt="loader" />
        <SatIcon v-else name="plus" />
        {{ $t("satellites.add") }}
      </button>
    </template>

    <div v-if="loading" class="d-flex justify-content-center py-5">
      <img :src="require('@/assets/images/rolling.svg')" alt="loader" class="loader" />
    </div>
    <template v-else>
      <SatellitesOnboarding :satellites="rows" :creating="creating" @create="create" @install="showInstall" />
      <div v-if="selected.length" class="sat-bulk">
        <span class="sat-bulk__count">{{ pl("satellites.counts.selected", selected.length) }}</span>
        <SatPopover :width="300">
          <template #trigger>
            <button class="btn _border _green sat-btn-inline" :disabled="bulkSaving"><SatIcon name="layers" /> {{ $t("satellites.bulk.move") }} <SatIcon name="chevron" :size="14" /></button>
          </template>
          <template #default="{ close }">
            <div class="sat-menu">
              <div class="sat-menu__label">{{ $t("satellites.bulk.move_to") }}</div>
              <button v-for="group in groups" :key="group.id" type="button" class="sat-menu__item" @click="close(), askMove(group.id)">
                <SatIcon name="layers" :size="14" />
                {{ group.name }}
                <span class="sat-menu__meta">{{ pl("satellites.counts.satellites", group.members_count) }}</span>
              </button>
              <button type="button" class="sat-menu__item" @click="close(), askMove(null)">
                <SatIcon name="server" :size="14" />
                {{ $t("satellites.groups.none") }}
              </button>
              <div class="sat-menu__divider"></div>
              <router-link :to="{ name: 'SatelliteGroup', params: { id: 'new' }, query: { satellites: selectedIds().join(',') } }" class="sat-menu__item">
                <SatIcon name="plus" :size="14" />
                {{ $t("satellites.bulk.new_group") }}
              </router-link>
            </div>
          </template>
        </SatPopover>
        <span class="sat-bulk__spacer"></span>
        <button type="button" class="sat-icon-btn" :title="$t('satellites.domains.clear_selection')" @click="clearSelection"><SatIcon name="x" /></button>
      </div>

      <div v-if="rows.length" class="c-table sat-table satellites-table mt-3">
        <div class="inner">
          <vue-good-table
            ref="table"
            styleClass="table  _no-wrap"
            :columns="columns"
            :rows="rows"
            :select-options="{ enabled: true, selectOnCheckboxOnly: true, disableSelectInfo: true }"
            @on-selected-rows-change="onSelectionChange"
          >
            <template slot="table-column" slot-scope="props">
              <span v-show="props.column.field !== 'control'">{{ formatLabel(props.column.label) }}</span>
            </template>

            <template slot="table-row" slot-scope="props">
              <template v-if="props.column.field === 'status'">
                <SatelliteStatus :status="props.row.status" />
              </template>

              <template v-else-if="props.column.field === 'cpu_load'">
                <CpuLoadChart v-if="props.row.status === 'online' && props.row.cpu_load" :load="props.row.cpu_load" />
                <span v-else class="sat-muted">—</span>
              </template>

              <template v-else-if="props.column.field === 'name'">
                <router-link :to="{ name: 'Satellite', params: { id: props.row.id } }" class="satellites-name">
                  {{ satName(props.row) }}
                </router-link>
                <span class="satellites-sub sat-mono">
                  <CopyIp v-if="props.row.ip" :ip="props.row.ip" />
                  <template v-else>{{ $t("satellites.awaiting_install") }}</template>
                </span>
              </template>

              <template v-else-if="props.column.field === 'group'">
                <router-link v-if="props.row.group" :to="{ name: 'SatelliteGroup', params: { id: props.row.group.id } }" class="sat-chip _accent satellites-group">
                  <SatIcon name="layers" :size="12" /> {{ props.row.group.name }}
                </router-link>
                <span v-else class="sat-muted">{{ $t("satellites.groups.none") }}</span>
              </template>

              <template v-else-if="props.column.field === 'domains_count'">
                <span :class="{ 'sat-muted': !props.row.domains_count }">{{ props.row.domains_count }}</span>
              </template>

              <template v-else-if="props.column.field === 'config_status'">
                <ConfigStatus v-if="props.row.installed_at" :status="props.row.config_status" />
                <span v-else class="sat-muted">—</span>
              </template>

              <template v-else-if="props.column.field === 'agent_version'">
                <span v-if="props.row.agent_version || props.row.os || props.row.arch" class="satellites-hinted" v-tooltip="{ content: systemHint(props.row), html: true, triggers: ['hover', 'click'] }">
                  {{ props.row.agent_version || "—" }}
                </span>
                <span v-else class="sat-muted">—</span>
                <AgentUpdateBadge :update="props.row.agent_update" />
              </template>

              <template v-else-if="props.column.field === 'updated_at'">
                <span class="satellites-hinted" v-tooltip="{ content: datesHint(props.row), html: true, triggers: ['hover', 'click'] }">
                  {{ formatDate(props.row.last_heartbeat_at || props.row.installed_at || props.row.created_at) }}
                </span>
              </template>

              <template v-else-if="props.column.field === 'control'">
                <div class="controls-wrapper">
                  <button v-if="props.row.install_command" @click="showInstall(props.row.install_command)" class="btn _border _green satellites-install-btn">
                    {{ $t("satellites.install_command") }}
                  </button>
                  <button v-if="canRequestAgentUpdate(props.row)" @click="askAgentUpdate(props.row)" class="sat-icon-btn satellites-update-btn" :title="$t('satellites.update.button_version', { version: props.row.agent_update.latest_version })">
                    <SatIcon name="arrow-up-circle" />
                  </button>
                  <button @click="askDelete(props.row)" class="sat-icon-btn _danger" :title="$t('satellites.delete')">
                    <SatIcon name="trash" />
                  </button>
                </div>
              </template>
            </template>
            <div class="d-flex justify-content-center" slot="emptystate">
              {{ $t("any_results") }}
            </div>
          </vue-good-table>
        </div>
      </div>
    </template>

    <InstallCommandModal :command="installCommand" />
    <AgentUpdateModal :satellite="updateTarget" @updated="fetch()" />

    <modal @closed="deleteTarget = null" :width="550" name="satelliteDelete" height="auto" :scrollable="true" classes="creative-modal">
      <div class="pt-3 pb-4 px-3 mx-auto d-block text-center">
        <svg v-svg @click="$modal.hide('satelliteDelete')" class="close" symbol="modal-close" size="0 0 15 15" role="info"></svg>
        <h3 class="modal-title pt-3">{{ $t("satellites.delete_confirm") }}</h3>
        <div v-if="deleteTarget" class="satellites-delete-ip pt-2">{{ satName(deleteTarget) }}</div>
        <button class="btn mt-5 mr-3 _bg-gray" @click="$modal.hide('satelliteDelete')">{{ $t("cancel") }}</button>
        <button class="btn mt-5 _bg-green" :disabled="deleting" @click="remove">{{ $t("satellites.delete") }}</button>
      </div>
    </modal>

    <modal :width="560" name="satelliteMove" height="auto" :scrollable="true" classes="creative-modal">
      <div class="pt-3 pb-4 px-4 satellites-move">
        <svg v-svg @click="$modal.hide('satelliteMove')" class="close" symbol="modal-close" size="0 0 15 15" role="info"></svg>
        <h3 class="modal-title pt-3">{{ moveTitle }}</h3>
        <div class="sat-note mt-3" :class="{ _warn: !!moveGroupId }">
          <SatIcon :name="moveGroupId ? 'alert' : 'info'" />
          <span>{{ moveGroupId ? $t("satellites.bulk.join_note") : $t("satellites.bulk.leave_note") }}</span>
        </div>
        <div class="d-flex justify-content-end mt-4" style="gap: 10px">
          <button class="btn _bg-gray" @click="$modal.hide('satelliteMove')">{{ $t("cancel") }}</button>
          <button class="btn _bg-green" :disabled="bulkSaving" @click="move">{{ $t("satellites.bulk.move") }}</button>
        </div>
      </div>
    </modal>
  </SatellitesPage>
</template>

<script>
import satelliteService from "@/api/satelliteService";
import SatellitesPage from "@/components/Satellites/SatellitesPage.vue";
import SatIcon from "@/components/Satellites/SatIcon.vue";
import CopyIp from "@/components/Satellites/CopyIp.vue";
import SatPopover from "@/components/Satellites/SatPopover.vue";
import SatellitesOnboarding from "@/components/Satellites/SatellitesOnboarding.vue";
import SatelliteStatus from "@/components/Satellites/SatelliteStatus.vue";
import InstallCommandModal from "@/components/Satellites/InstallCommandModal.vue";
import ConfigStatus from "@/components/Satellites/ConfigStatus.vue";
import CpuLoadChart from "@/components/Satellites/CpuLoadChart.vue";
import AgentUpdateBadge from "@/components/Satellites/AgentUpdateBadge.vue";
import AgentUpdateModal from "@/components/Satellites/AgentUpdateModal.vue";
import { canRequestAgentUpdate } from "@/components/Satellites/agentUpdate";
import satelliteFeatureGuard from "@/components/Satellites/satelliteFeatureGuard";
import { formatMixin, isOk } from "@/components/Satellites/format";

const POLL_INTERVAL = 10000;
const escapeHtml = (value) => String(value).replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`);

export default {
  name: "Satellites",
  components: { CopyIp, SatellitesPage, SatIcon, SatPopover, SatellitesOnboarding, SatelliteStatus, InstallCommandModal, ConfigStatus, CpuLoadChart, AgentUpdateBadge, AgentUpdateModal },
  mixins: [formatMixin],
  data() {
    return {
      rows: [],
      loading: true,
      creating: false,
      deleting: false,
      deleteTarget: null,
      updateTarget: null,
      installCommand: "",
      pollTimer: null,
      // Bulk actions
      selected: [],
      groups: [],
      moveGroupId: null,
      bulkSaving: false,
      columns: [
        {
          label: "satellites.columns.name",
          field: "name",
          sortable: false,
        },
        {
          label: "satellites.columns.status",
          field: "status",
          sortable: false,
        },
        {
          label: "satellites.columns.cpu",
          field: "cpu_load",
          sortable: false,
        },
        {
          label: "satellites.columns.group",
          field: "group",
          sortable: false,
        },
        {
          label: "satellites.columns.domains",
          field: "domains_count",
          sortable: false,
        },
        {
          label: "satellites.columns.config",
          field: "config_status",
          sortable: false,
        },
        {
          label: "satellites.columns.agent_version",
          field: "agent_version",
          sortable: false,
        },
        {
          label: "satellites.columns.updated_at",
          field: "updated_at",
          sortable: false,
        },
        {
          label: "",
          field: "control",
          sortable: false,
        },
      ],
    };
  },
  beforeRouteEnter: satelliteFeatureGuard,
  computed: {
    moveTitle() {
      const count = this.pl("satellites.counts.satellites", this.selected.length);
      const group = this.groups.find((g) => g.id === this.moveGroupId);
      return group ? this.$t("satellites.bulk.move_title", { count, group: group.name }) : this.$t("satellites.bulk.leave_title", { count });
    },
  },
  mounted() {
    this.fetch(true);
    this.fetchGroups();
    // Refreshing the rows drops the selection, so polling pauses while satellites are selected.
    this.pollTimer = setInterval(() => {
      if (!this.selected.length) this.fetch();
    }, POLL_INTERVAL);
  },
  beforeDestroy() {
    clearInterval(this.pollTimer);
  },
  methods: {
    formatLabel(el) {
      return this.$t(el);
    },
    // Tooltip body for html tooltips: one "title: value" line per item, values come from the agent so they are escaped.
    hintLines(lines) {
      return lines
        .map(([label, value]) => `<span class="satellites-hint__line"><span class="satellites-hint__title">${this.$t(label)}:</span> ${value ? escapeHtml(value) : "—"}</span>`)
        .join("");
    },
    systemHint(row) {
      return this.hintLines([
        ["satellites.columns.agent_version", row.agent_version],
        ["satellites.columns.os", [row.os, row.os_version].filter(Boolean).join(" ")],
        ["satellites.columns.arch", row.arch],
      ]);
    },
    datesHint(row) {
      const date = (value) => value && this.formatDate(value);
      return this.hintLines([
        ["satellites.columns.created_at", date(row.created_at)],
        ["satellites.columns.installed_at", date(row.installed_at)],
        ["satellites.columns.last_heartbeat", date(row.last_heartbeat_at)],
      ]);
    },
    fetch(initial = false) {
      return satelliteService
        .getSatellites()
        .then((res) => {
          if (isOk(res)) {
            this.rows = Array.isArray(res.data) ? res.data : [];
          } else if (res && res.status === 403) {
            this.$router.replace({ name: "Dashboard" }).catch(() => {});
          } else if (initial) {
            this.showError(res);
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    create() {
      if (this.creating) return;
      this.creating = true;
      satelliteService
        .createSatellite()
        .then((res) => {
          if (isOk(res) && res.data) {
            this.$alert({
              type: "success",
              title: this.$t("satellites.created"),
            });
            this.fetch();
            if (res.data.install_command) {
              this.showInstall(res.data.install_command);
            }
          } else {
            this.showError(res);
          }
        })
        .finally(() => {
          this.creating = false;
        });
    },
    showInstall(command) {
      this.installCommand = command;
      this.$modal.show("satelliteInstall");
    },
    canRequestAgentUpdate,
    askAgentUpdate(row) {
      this.updateTarget = row;
      this.$modal.show("satelliteAgentUpdate");
    },
    askDelete(row) {
      this.deleteTarget = row;
      this.$modal.show("satelliteDelete");
    },
    fetchGroups() {
      satelliteService.getGroups().then((res) => {
        if (isOk(res)) this.groups = Array.isArray(res.data) ? res.data : [];
      });
    },
    onSelectionChange(params) {
      this.selected = params.selectedRows;
    },
    clearSelection() {
      this.selected = [];
      if (this.$refs.table) this.$refs.table.unselectAllInternal();
    },
    selectedIds() {
      return this.selected.map((row) => row.id);
    },
    askMove(groupId) {
      this.moveGroupId = groupId;
      // Satellites already in the target group stay as they are: no need to ask when nothing changes for them.
      const changing = this.selected.filter((row) => (row.group ? row.group.id : null) !== groupId);
      if (!changing.length) return;
      this.$modal.show("satelliteMove");
    },
    move() {
      if (this.bulkSaving) return;
      this.bulkSaving = true;
      satelliteService
        .moveSatellites(this.selectedIds(), this.moveGroupId)
        .then((res) => {
          if (isOk(res)) {
            this.$modal.hide("satelliteMove");
            this.$alert({ type: "success", title: this.$t("satellites.bulk.moved") });
            this.clearSelection();
            this.fetch();
            this.fetchGroups();
          } else {
            this.showError(res);
          }
        })
        .finally(() => {
          this.bulkSaving = false;
        });
    },
    remove() {
      if (!this.deleteTarget || this.deleting) return;
      this.deleting = true;
      satelliteService
        .deleteSatellite(this.deleteTarget.id)
        .then((res) => {
          if (isOk(res)) {
            this.$alert({
              type: "success",
              title: this.$t("satellites.deleted"),
            });
            this.$modal.hide("satelliteDelete");
            this.fetch();
          } else {
            this.showError(res);
          }
        })
        .finally(() => {
          this.deleting = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.satellites-ip {
  font-weight: 600;
  color: var(--sat-text);
}

.satellites-hinted {
  cursor: default;
  border-bottom: 1px dashed currentColor;
}

.satellites-name {
  display: block;
  font-weight: 600;
  color: var(--sat-accent);
}

.satellites-sub {
  display: block;
  font-size: 12px;
  color: var(--sat-muted);
}

.satellites-group:hover {
  text-decoration: none;
}

.controls-wrapper {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.satellites-install-btn {
  height: 32px;
  padding: 0 14px;
  font-size: 13px;
  border-radius: 8px;
  white-space: nowrap;
}

.satellites-update-btn {
  color: var(--sat-accent);
}

.satellites-delete-ip {
  color: var(--sat-accent);
  font-weight: 500;
}

.satellites-move {
  position: relative;
}

body.dark .satellites-install-btn {
  color: #479495;
  border-color: #479495;

  &:hover {
    color: #fff;
  }
}
</style>

<style lang="scss">
// Tooltips are rendered outside the component, so their content styles can't be scoped.
.satellites-hint__line {
  display: block;
  white-space: nowrap;
}

.satellites-hint__title {
  color: #9b9ba4;
}
</style>
